/* =====================================================================
   VORTO — Dicionários PT / EN / ES
   ---------------------------------------------------------------------
   ESTE É O ARQUIVO ONDE VOCÊ EDITA OS TEXTOS DO SITE.

   • Cada texto tem uma "chave" (ex.: 'hero.sub') usada pelos componentes
     como t('hero.sub').
   • Para mudar um texto, ache a chave e edite os TRÊS idiomas (pt, en,
     es) — ficam lado a lado de propósito, pra não esquecer a tradução.
   • O idioma inicial vem do navegador (pt → PT, es → ES, resto → EN) e a
     escolha manual fica salva no localStorage.
   ===================================================================== */

export type Lang = "pt" | "en" | "es";
type Dict = Record<string, string>;

export const I18N: Record<Lang, Dict> = {
  /* =================================================================
     PORTUGUÊS
     ================================================================= */
  pt: {
    "meta.title": "Vorto - Transformação Digital para Sua Empresa",
    "meta.desc":
      "A Vorto é uma software house de produtos corporativos — a Central e o Boardyz — e constrói software sob medida com inteligência artificial na operação.",

    "nav.servicos": "Sob medida",
    "nav.central": "Produtos",
    "nav.processo": "Como funciona",
    "nav.seguranca": "Segurança",
    "nav.faq": "Dúvidas",
    "nav.cta": "Falar com a Vorto",
    "nav.menu": "Abrir menu",
    "nav.fechar": "Fechar menu",

    "hero.chip": "Software house · Produtos próprios em produção",
    "hero.titulo.a": "Construímos o ",
    "hero.titulo.destaque": "software",
    "hero.titulo.b": " que roda empresas.",
    "hero.sub":
      "A Vorto é uma software house com produtos corporativos próprios — a Central e o Boardyz. E é a mesma engenharia que constrói o sistema sob medida que a sua operação precisa, com IA de fábrica.",
    "hero.cta1": "Falar com a Vorto",
    "hero.cta2": "Ver os produtos",
    "hero.check1": "Time enxuto e sênior",
    "hero.check2": "Entrega de ponta a ponta",
    "hero.check3": "Seus dados não treinam modelos",

    "faixa.titulo": "Construímos nossa experiência a partir de empresas como",

    "serv.rotulo": "Sob medida",
    "serv.titulo": "Quando o de prateleira não serve, construímos o seu.",
    "serv.desc":
      "A mesma engenharia dos nossos produtos, aplicada ao seu problema: software, agentes de IA e integração — conectados aos sistemas que a sua empresa já usa.",
    "serv.c1.titulo": "Software sob medida",
    "serv.c1.desc":
      "Sistemas, portais e integrações feitos para o jeito que a sua empresa trabalha — já nascendo com inteligência artificial por dentro, não como remendo depois.",
    "serv.c1.li1": "Sistemas internos, portais e apps",
    "serv.c1.li2": "IA e agentes integrados desde o início",
    "serv.c1.li3": "Feito sob medida para o seu processo",
    "serv.c2.titulo": "Agentes de IA na operação",
    "serv.c2.desc":
      "Implementação de agentes que executam processos de ponta a ponta dentro da empresa — leem, decidem e agem nos seus sistemas, sem depender de gente para cada passo.",
    "serv.c2.li1": "Atendimento e triagem",
    "serv.c2.li2": "Entrada de pedidos e documentos",
    "serv.c2.li3": "Rotinas de back-office",
    "serv.c3.titulo": "Integração com seus sistemas",
    "serv.c3.desc":
      "Conectamos a IA aos ERPs, bancos de dados e ferramentas que você já usa — para ela trabalhar com informação real e atualizada, não em planilha à parte.",
    "serv.c3.li1": "Conexão com ERP e bancos de dados",
    "serv.c3.li2": "Integração com as ferramentas atuais",
    "serv.c3.li3": "Dados prontos para a IA agir",

    "prod.rotulo": "Produtos",
    "prod.titulo": "Produtos corporativos feitos — e operados — pela Vorto.",
    "prod.desc":
      "Não vendemos horas: mantemos produtos próprios rodando em produção. É essa engenharia que entra em cada projeto.",

    "central.rotulo": "O carro-chefe",
    "central.titulo": "Central: o roteador da sua empresa.",
    "central.desc":
      "Mais que um roteador de IA — a camada por onde a operação inteira passa. Dados, agentes, decisões e aprovações fluem pela Central: conectada aos seus bancos, ERPs e sistemas, ela executa o trabalho enquanto você governa tudo num painel só.",
    "central.i1.t": "Conectada aos seus sistemas",
    "central.i1.d":
      "Agentes ligados a bancos de dados, ERPs e ferramentas — leem e agem direto onde a operação já acontece.",
    "central.i2.t": "A operação inteira à vista",
    "central.i2.d":
      "Agentes ativos, conversas, custos, tokens e erros — em tempo real, num painel só.",
    "central.i3.t": "Você no controle",
    "central.i3.d":
      "Regras de quando o agente age sozinho ou pede aprovação, com auditoria de tudo que cada um fez.",
    "central.p.badge": "Central · Visão geral",
    "central.p.hello": "Boa noite, Vinícius",
    "central.p.live": "14 agentes ativos · 715 conversas ao vivo",
    "central.p.online": "sistema online",
    "central.p.s1": "Agentes ativos",
    "central.p.s2": "Conversas ao vivo",
    "central.p.s3": "Mensagens 24h",
    "central.p.r1": "Agente de Atendimento",
    "central.p.r2": "Validador de faturamento",
    "central.p.fl.t": "Pedido #4821 · aguardando sua aprovação",
    "central.p.fl.a": "Aprovar",
    "central.p.fl.r": "Revisar",

    "bz.chip": "Produto privado · Em operação",
    "bz.titulo": "Boardyz: os projetos do seu time, organizados num quadro só.",
    "bz.desc":
      "Um kanban corporativo e exclusivo: cada empresa tem o seu próprio ambiente, com controle de quem entra, o que vê e o que pode fazer. O time trabalha em tempo real — e os assistentes de IA também: criam, movem e atualizam cartões, sempre com a permissão de quem os usa.",
    "bz.li1": "Quadros, checklists e prazos em tempo real",
    "bz.li2": "Ambiente exclusivo da sua empresa, com auditoria completa",
    "bz.li3": "Sua IA cria e move cartões — Claude, ChatGPT, Cursor",
    "bz.cta": "Quero o Boardyz na minha empresa",

    "proc.rotulo": "Como funciona",
    "proc.titulo": "Do diagnóstico à operação no ar.",
    "proc.desc":
      "Um caminho curto e sem mistério. Você sabe o que vem em cada etapa e o que já está entregando valor.",
    "proc.p1.t": "Diagnóstico",
    "proc.p1.d":
      "Entendemos a operação e apontamos onde a automação paga a conta mais rápido. Você recebe uma recomendação, não um cardápio.",
    "proc.p2.t": "Construção",
    "proc.p2.d":
      "Montamos o software e os agentes sob medida, integrados aos sistemas que você já usa. Entregas em semanas, não em meses.",
    "proc.p3.t": "Automação",
    "proc.p3.d":
      "Colocamos os agentes para executar de verdade, com regras claras de quando agem sozinhos e quando pedem sua aprovação.",
    "proc.p4.t": "Operação",
    "proc.p4.d":
      "Tudo vive na Central, com métricas à vista. A gente acompanha, ajusta e evolui junto — respondendo como dono.",

    "dif.rotulo": "Por que Vorto",
    "dif.titulo": "Respondemos como donos, não como fornecedor de horas.",
    "dif.d1.t": "Time enxuto e sênior",
    "dif.d1.d":
      "Você fala direto com quem constrói. Sem camada de gerência de projeto para atravessar e sem estagiário aprendendo no seu contrato.",
    "dif.d2.t": "Foco em resultado",
    "dif.d2.d":
      "Falamos em pedido que entra sozinho no ERP e em dia de trabalho economizado — não em horas faturadas. O objetivo é o seu, a gente cuida do caminho.",
    "dif.d3.t": "De ponta a ponta",
    "dif.d3.d":
      "Do primeiro diagnóstico à operação rodando, é a mesma equipe. Nada de jogar a bola para um outro time na hora de sustentar o que foi feito.",
    "dif.d4.t": "Respostas na hora",
    "dif.d4.d":
      "Transformação digital que responde rápido. Problema em produção não vira ticket perdido numa fila — vira prioridade.",

    "seg.rotulo": "Segurança",
    "seg.titulo": "Confiança não se promete, se comprova.",
    "seg.desc":
      "Quando o assunto é o dado da sua empresa, a régua é a de comprador enterprise. Trabalhamos dentro das suas regras — e deixamos tudo registrado.",
    "seg.s1": "Conformidade com a LGPD",
    "seg.s2": "Seus dados não treinam modelos",
    "seg.s3": "Controle de acesso e SSO",
    "seg.s4": "Código e infraestrutura auditáveis",

    "case.rotulo": "Resultado",
    "case.titulo":
      "Um agente da Vorto já roda no pós-venda de uma concessionária.",
    "case.sub":
      "Um agente de IA especialista que responde no WhatsApp da equipe — com o conhecimento técnico da operação, na hora e com a fonte.",
    "case.quote":
      "“Antes eu parava a máquina e esperava horas por um retorno. Agora mando no WhatsApp e já sigo o reparo.”",
    "case.nome": "Carlos",
    "case.cargo": "Mecânico de campo · Concessionária",
    "case.m1.v": "+2.000",
    "case.m1.l": "interações por semana no WhatsApp",
    "case.m2.v": "80%",
    "case.m2.l": "das dúvidas resolvidas sem acionar o especialista",
    "case.m3.v": "~12 min",
    "case.m3.l": "economizados a cada consulta técnica",
    "case.mini":
      "+180 profissionais usando · 3× mais rápido para achar a peça · −55% de escalonamentos · 24/7 no WhatsApp",

    "faq.rotulo": "Dúvidas",
    "faq.titulo": "Perguntas que sempre aparecem.",
    "faq.q1.q": "Vocês são uma agência ou uma consultoria?",
    "faq.q1.a":
      "Nenhuma das duas. Somos uma software house: construímos o software e automatizamos a operação de ponta a ponta, com um time enxuto e sênior que responde como dono do resultado — não como fornecedor de horas.",
    "faq.q2.q": "Preciso ter um time de tecnologia interno?",
    "faq.q2.a":
      "Não. A Vorto existe justamente para empresas que precisam se modernizar mas não têm (nem querem montar) um time de tecnologia. A gente cuida da construção e da operação; você mantém o controle pela Central.",
    "faq.q3.q": "Quanto tempo até ver algo funcionando?",
    "faq.q3.a":
      "Trabalhamos em entregas de semanas, não de meses. Depois do diagnóstico, começamos pelo módulo que paga o projeto mais rápido — o primeiro agente costuma ir ao ar já nas primeiras semanas.",
    "faq.q4.q": "Os agentes de IA usam os dados da minha empresa para treinar modelos?",
    "faq.q4.a":
      "Não. Seus dados não treinam modelos. Trabalhamos dentro das suas regras de segurança, com conformidade à LGPD, controle de acesso e tudo registrado para auditoria.",
    "faq.q5.q": "E se a IA errar? Quem fica no comando?",
    "faq.q5.a":
      "Você. Cada agente tem regras claras de quando pode agir sozinho e quando precisa de aprovação humana. Tudo passa pela Central, onde você acompanha, aprova e ajusta.",
    "faq.q6.q": "Vocês integram com os sistemas que já usamos?",
    "faq.q6.a":
      "Sim. Construímos em cima do que você já tem — ERP, planilhas, ferramentas de atendimento — em vez de exigir que todo mundo troque de sistema. A automação entra no fluxo atual.",

    "cta.rotulo": "Vamos conversar",
    "cta.titulo": "Pronto para a virada digital da sua empresa?",
    "cta.desc":
      "Conte o gargalo que mais te incomoda hoje. A gente aponta por onde começar e o que a automação resolve primeiro — sem compromisso.",
    "cta.btn2": "Agendar uma conversa",
    "cta.form.nome": "Nome",
    "cta.form.nomePh": "Seu nome",
    "cta.form.email": "E-mail",
    "cta.form.emailPh": "voce@empresa.com",
    "cta.form.empresa": "Empresa",
    "cta.form.empresaPh": "Opcional",
    "cta.form.msg": "Qual o seu maior gargalo hoje?",
    "cta.form.msgPh":
      "Conte em uma ou duas frases o que mais consome o tempo do time.",
    "cta.form.enviar": "Enviar mensagem",
    "cta.form.enviando": "Enviando…",
    "cta.form.ok": "Recebido! Retornamos em breve.",
    "cta.form.erro":
      "Algo deu errado. Tente de novo ou escreva para contact@vortolabs.com.",
    "cta.form.invalido": "Preencha nome, e-mail válido e mensagem.",

    "footer.sobre": "Software house de produtos corporativos. Software que roda empresas.",
    "footer.h1": "Navegação",
    "footer.h2": "Empresa",
    "footer.h3": "Contato",
    "footer.results": "Resultados",
    "footer.contact": "Contato",
    "footer.base": "© 2026 Vorto Labs. Todos os direitos reservados.",
  },

  /* =================================================================
     ENGLISH
     ================================================================= */
  en: {
    "meta.title": "Vorto - Digital Transformation for Your Business",
    "meta.desc":
      "Vorto is a software house with corporate products of its own — the Central and Boardyz — and builds custom software with artificial intelligence in the operation.",

    "nav.servicos": "Custom work",
    "nav.central": "Products",
    "nav.processo": "How it works",
    "nav.seguranca": "Security",
    "nav.faq": "FAQ",
    "nav.cta": "Talk to Vorto",
    "nav.menu": "Open menu",
    "nav.fechar": "Close menu",

    "hero.chip": "Software house · Our own products in production",
    "hero.titulo.a": "We build the ",
    "hero.titulo.destaque": "software",
    "hero.titulo.b": " that runs companies.",
    "hero.sub":
      "Vorto is a software house with corporate products of its own — the Central and Boardyz. The same engineering builds the custom system your operation needs, with AI built in from day one.",
    "hero.cta1": "Talk to Vorto",
    "hero.cta2": "See the products",
    "hero.check1": "Small, senior team",
    "hero.check2": "End-to-end delivery",
    "hero.check3": "Your data never trains models",

    "faixa.titulo": "We built our experience at companies like",

    "serv.rotulo": "Custom work",
    "serv.titulo": "When off-the-shelf won’t do, we build yours.",
    "serv.desc":
      "The same engineering behind our products, applied to your problem: software, AI agents and integration — connected to the systems your company already uses.",
    "serv.c1.titulo": "Custom software",
    "serv.c1.desc":
      "Systems, portals and integrations built for the way your company works — born with AI inside, not bolted on later.",
    "serv.c1.li1": "Internal systems, portals and apps",
    "serv.c1.li2": "AI and agents integrated from the start",
    "serv.c1.li3": "Tailored to your process",
    "serv.c2.titulo": "AI agents in your operation",
    "serv.c2.desc":
      "We deploy agents that run processes end to end inside your company — reading, deciding and acting in your systems, without a person for every step.",
    "serv.c2.li1": "Support and triage",
    "serv.c2.li2": "Order and document intake",
    "serv.c2.li3": "Back-office routines",
    "serv.c3.titulo": "Integration with your systems",
    "serv.c3.desc":
      "We connect AI to the ERPs, databases and tools you already use — so it works on real, up-to-date information, not a spreadsheet on the side.",
    "serv.c3.li1": "Connection to ERP and databases",
    "serv.c3.li2": "Integration with your current tools",
    "serv.c3.li3": "Data ready for AI to act on",

    "prod.rotulo": "Products",
    "prod.titulo": "Corporate products built — and operated — by Vorto.",
    "prod.desc":
      "We don’t sell hours: we keep products of our own running in production. That engineering goes into every project.",

    "central.rotulo": "The flagship",
    "central.titulo": "Central: your company’s router.",
    "central.desc":
      "More than an AI router — the layer your whole operation flows through. Data, agents, decisions and approvals pass through the Central: connected to your databases, ERPs and systems, it does the work while you govern everything from one panel.",
    "central.i1.t": "Connected to your systems",
    "central.i1.d":
      "Agents wired to databases, ERPs and tools — they read and act right where your operation already runs.",
    "central.i2.t": "Your whole operation in view",
    "central.i2.d":
      "Active agents, conversations, cost, tokens and errors — in real time, in one panel.",
    "central.i3.t": "You stay in control",
    "central.i3.d":
      "Rules for when an agent acts alone or asks for approval, with an audit of everything each one did.",
    "central.p.badge": "Central · Overview",
    "central.p.hello": "Good evening, Vinícius",
    "central.p.live": "14 active agents · 715 live conversations",
    "central.p.online": "system online",
    "central.p.s1": "Active agents",
    "central.p.s2": "Live conversations",
    "central.p.s3": "Messages 24h",
    "central.p.r1": "Support Agent",
    "central.p.r2": "Billing Validator",
    "central.p.fl.t": "Order #4821 · awaiting your approval",
    "central.p.fl.a": "Approve",
    "central.p.fl.r": "Review",

    "bz.chip": "Private product · In production",
    "bz.titulo": "Boardyz: your team’s projects, organized on one board.",
    "bz.desc":
      "A corporate, exclusive kanban: each company gets its own environment, controlling who gets in, what they see and what they can do. Your team works in real time — and so do AI assistants: they create, move and update cards, always with the permissions of the person using them.",
    "bz.li1": "Boards, checklists and deadlines in real time",
    "bz.li2": "Your company’s exclusive environment, fully audited",
    "bz.li3": "Your AI creates and moves cards — Claude, ChatGPT, Cursor",
    "bz.cta": "Get Boardyz for your company",

    "proc.rotulo": "How it works",
    "proc.titulo": "From diagnosis to a live operation.",
    "proc.desc":
      "A short, no-mystery path. You know what comes at each stage and what’s already delivering value.",
    "proc.p1.t": "Diagnosis",
    "proc.p1.d":
      "We study your operation and point to where automation pays off fastest. You get a recommendation, not a menu.",
    "proc.p2.t": "Build",
    "proc.p2.d":
      "We build the custom software and agents, integrated with the systems you already use. Delivered in weeks, not months.",
    "proc.p3.t": "Automation",
    "proc.p3.d":
      "We put the agents to real work, with clear rules for when they act alone and when they ask for your approval.",
    "proc.p4.t": "Operation",
    "proc.p4.d":
      "Everything lives in the Central, metrics in plain sight. We monitor, tune and evolve with you — answering like owners.",

    "dif.rotulo": "Why Vorto",
    "dif.titulo": "We answer like owners, not like an hours vendor.",
    "dif.d1.t": "Small, senior team",
    "dif.d1.d":
      "You talk straight to whoever builds it. No project-management layer to cross and no intern learning on your contract.",
    "dif.d2.t": "Focused on results",
    "dif.d2.d":
      "We talk in orders that post themselves and workdays saved — not billed hours. The goal is yours; we handle the path.",
    "dif.d3.t": "End to end",
    "dif.d3.d":
      "From the first diagnosis to a running operation, it’s the same team. No handing off to someone else when it’s time to maintain it.",
    "dif.d4.t": "Answers on the spot",
    "dif.d4.d":
      "Digital transformation that responds fast. A production issue doesn’t become a lost ticket in a queue — it becomes a priority.",

    "seg.rotulo": "Security",
    "seg.titulo": "Trust isn’t promised, it’s proven.",
    "seg.desc":
      "When it comes to your company’s data, the bar is the enterprise buyer’s. We work within your rules — and keep everything on record.",
    "seg.s1": "Compliance with data protection laws",
    "seg.s2": "Your data never trains models",
    "seg.s3": "Access control and SSO",
    "seg.s4": "Auditable code and infrastructure",

    "case.rotulo": "Results",
    "case.titulo": "A Vorto agent already runs a dealership’s after-sales.",
    "case.sub":
      "A specialist AI agent that answers on the team’s WhatsApp — with the operation’s technical knowledge, instantly and with the source.",
    "case.quote":
      "“I used to stop the machine and wait hours for an answer. Now I message on WhatsApp and get straight back to the repair.”",
    "case.nome": "Carlos",
    "case.cargo": "Field mechanic · Dealership",
    "case.m1.v": "+2,000",
    "case.m1.l": "interactions per week on WhatsApp",
    "case.m2.v": "80%",
    "case.m2.l": "of questions solved without the specialist",
    "case.m3.v": "~12 min",
    "case.m3.l": "saved on every technical query",
    "case.mini":
      "+180 professionals using it · 3× faster to find the right part · −55% escalations · 24/7 on WhatsApp",

    "faq.rotulo": "FAQ",
    "faq.titulo": "Questions that always come up.",
    "faq.q1.q": "Are you an agency or a consultancy?",
    "faq.q1.a":
      "Neither. We’re a software house: we build the software and automate the operation end to end, with a small, senior team that answers like an owner of the result — not an hours vendor.",
    "faq.q2.q": "Do I need an in-house tech team?",
    "faq.q2.a":
      "No. Vorto exists exactly for companies that need to modernize but don’t have (and don’t want to build) a tech team. We handle the build and the operation; you keep control through the Central.",
    "faq.q3.q": "How long until I see something working?",
    "faq.q3.a":
      "We work in deliveries of weeks, not months. After the diagnosis, we start with the module that pays off fastest — the first agent usually goes live within the first few weeks.",
    "faq.q4.q": "Do the AI agents use my company’s data to train models?",
    "faq.q4.a":
      "No. Your data never trains models. We work within your security rules, in line with data protection laws, access control and everything logged for audit.",
    "faq.q5.q": "What if the AI gets it wrong? Who’s in command?",
    "faq.q5.a":
      "You are. Each agent has clear rules for when it can act alone and when it needs human approval. Everything goes through the Central, where you monitor, approve and adjust.",
    "faq.q6.q": "Do you integrate with the systems we already use?",
    "faq.q6.a":
      "Yes. We build on top of what you already have — ERP, spreadsheets, support tools — instead of forcing everyone onto a new system. Automation fits into your current flow.",

    "cta.rotulo": "Let’s talk",
    "cta.titulo": "Ready for your company’s digital turn?",
    "cta.desc":
      "Tell us the bottleneck that bothers you most today. We’ll point out where to start and what automation solves first — no strings attached.",
    "cta.btn2": "Book a call",
    "cta.form.nome": "Name",
    "cta.form.nomePh": "Your name",
    "cta.form.email": "Email",
    "cta.form.emailPh": "you@company.com",
    "cta.form.empresa": "Company",
    "cta.form.empresaPh": "Optional",
    "cta.form.msg": "What’s your biggest bottleneck today?",
    "cta.form.msgPh":
      "In a sentence or two, tell us what eats up the team’s time.",
    "cta.form.enviar": "Send message",
    "cta.form.enviando": "Sending…",
    "cta.form.ok": "Got it! We’ll get back to you soon.",
    "cta.form.erro":
      "Something went wrong. Try again or email contact@vortolabs.com.",
    "cta.form.invalido": "Please fill in name, a valid email and a message.",

    "footer.sobre": "Software house with corporate products. Software that runs companies.",
    "footer.h1": "Navigation",
    "footer.h2": "Company",
    "footer.h3": "Contact",
    "footer.results": "Results",
    "footer.contact": "Contact",
    "footer.base": "© 2026 Vorto Labs. All rights reserved.",
  },

  /* =================================================================
     ESPAÑOL (México)
     ================================================================= */
  es: {
    "meta.title": "Vorto - Transformación Digital para Tu Empresa",
    "meta.desc":
      "Vorto es una software house con productos corporativos propios — la Central y Boardyz — y construye software a medida con inteligencia artificial en la operación.",

    "nav.servicos": "A medida",
    "nav.central": "Productos",
    "nav.processo": "Cómo funciona",
    "nav.seguranca": "Seguridad",
    "nav.faq": "Preguntas",
    "nav.cta": "Hablar con Vorto",
    "nav.menu": "Abrir menú",
    "nav.fechar": "Cerrar menú",

    "hero.chip": "Software house · Productos propios en producción",
    "hero.titulo.a": "Construimos el ",
    "hero.titulo.destaque": "software",
    "hero.titulo.b": " que hace funcionar empresas.",
    "hero.sub":
      "Vorto es una software house con productos corporativos propios — la Central y Boardyz. La misma ingeniería construye el sistema a medida que tu operación necesita, con IA de fábrica.",
    "hero.cta1": "Hablar con Vorto",
    "hero.cta2": "Ver los productos",
    "hero.check1": "Equipo reducido y sénior",
    "hero.check2": "Entrega de punta a punta",
    "hero.check3": "Tus datos no entrenan modelos",

    "faixa.titulo": "Construimos nuestra experiencia en empresas como",

    "serv.rotulo": "A medida",
    "serv.titulo": "Cuando lo de estante no alcanza, construimos el tuyo.",
    "serv.desc":
      "La misma ingeniería de nuestros productos, aplicada a tu problema: software, agentes de IA e integración — conectados a los sistemas que tu empresa ya usa.",
    "serv.c1.titulo": "Software a medida",
    "serv.c1.desc":
      "Sistemas, portales e integraciones hechos para la forma en que tu empresa trabaja — nacen con inteligencia artificial por dentro, no como parche después.",
    "serv.c1.li1": "Sistemas internos, portales y apps",
    "serv.c1.li2": "IA y agentes integrados desde el inicio",
    "serv.c1.li3": "Hecho a la medida de tu proceso",
    "serv.c2.titulo": "Agentes de IA en tu operación",
    "serv.c2.desc":
      "Implementamos agentes que ejecutan procesos de punta a punta dentro de la empresa — leen, deciden y actúan en tus sistemas, sin depender de una persona en cada paso.",
    "serv.c2.li1": "Atención y triaje",
    "serv.c2.li2": "Captura de pedidos y documentos",
    "serv.c2.li3": "Rutinas de back-office",
    "serv.c3.titulo": "Integración con tus sistemas",
    "serv.c3.desc":
      "Conectamos la IA a los ERP, bases de datos y herramientas que ya usas — para que trabaje con información real y actualizada, no en una hoja de cálculo aparte.",
    "serv.c3.li1": "Conexión con ERP y bases de datos",
    "serv.c3.li2": "Integración con tus herramientas actuales",
    "serv.c3.li3": "Datos listos para que la IA actúe",

    "prod.rotulo": "Productos",
    "prod.titulo": "Productos corporativos hechos — y operados — por Vorto.",
    "prod.desc":
      "No vendemos horas: mantenemos productos propios funcionando en producción. Esa ingeniería entra en cada proyecto.",

    "central.rotulo": "El buque insignia",
    "central.titulo": "Central: el router de tu empresa.",
    "central.desc":
      "Más que un router de IA — la capa por donde pasa toda la operación. Datos, agentes, decisiones y aprobaciones fluyen por la Central: conectada a tus bases de datos, ERP y sistemas, ejecuta el trabajo mientras tú gobiernas todo desde un solo panel.",
    "central.i1.t": "Conectada a tus sistemas",
    "central.i1.d":
      "Agentes enlazados a bases de datos, ERP y herramientas — leen y actúan justo donde la operación ya ocurre.",
    "central.i2.t": "Toda la operación a la vista",
    "central.i2.d":
      "Agentes activos, conversaciones, costos, tokens y errores — en tiempo real, en un solo panel.",
    "central.i3.t": "Tú tienes el control",
    "central.i3.d":
      "Reglas de cuándo el agente actúa solo o pide aprobación, con auditoría de todo lo que hizo cada uno.",
    "central.p.badge": "Central · Vista general",
    "central.p.hello": "Buenas noches, Vinícius",
    "central.p.live": "14 agentes activos · 715 conversaciones en vivo",
    "central.p.online": "sistema en línea",
    "central.p.s1": "Agentes activos",
    "central.p.s2": "Conversaciones en vivo",
    "central.p.s3": "Mensajes 24 h",
    "central.p.r1": "Agente de Atención",
    "central.p.r2": "Validador de facturación",
    "central.p.fl.t": "Pedido #4821 · esperando tu aprobación",
    "central.p.fl.a": "Aprobar",
    "central.p.fl.r": "Revisar",

    "bz.chip": "Producto privado · En producción",
    "bz.titulo": "Boardyz: los proyectos de tu equipo, organizados en un solo tablero.",
    "bz.desc":
      "Un kanban corporativo y exclusivo: cada empresa tiene su propio entorno, con control de quién entra, qué ve y qué puede hacer. Tu equipo trabaja en tiempo real — y los asistentes de IA también: crean, mueven y actualizan tarjetas, siempre con los permisos de quien los usa.",
    "bz.li1": "Tableros, checklists y plazos en tiempo real",
    "bz.li2": "Entorno exclusivo de tu empresa, con auditoría completa",
    "bz.li3": "Tu IA crea y mueve tarjetas — Claude, ChatGPT, Cursor",
    "bz.cta": "Quiero Boardyz en mi empresa",

    "proc.rotulo": "Cómo funciona",
    "proc.titulo": "Del diagnóstico a la operación en marcha.",
    "proc.desc":
      "Un camino corto y sin misterio. Sabes qué viene en cada etapa y qué ya está entregando valor.",
    "proc.p1.t": "Diagnóstico",
    "proc.p1.d":
      "Entendemos la operación y señalamos dónde la automatización se paga más rápido. Recibes una recomendación, no un menú.",
    "proc.p2.t": "Construcción",
    "proc.p2.d":
      "Montamos el software y los agentes a medida, integrados a los sistemas que ya usas. Entregas en semanas, no en meses.",
    "proc.p3.t": "Automatización",
    "proc.p3.d":
      "Ponemos a los agentes a ejecutar de verdad, con reglas claras de cuándo actúan solos y cuándo piden tu aprobación.",
    "proc.p4.t": "Operación",
    "proc.p4.d":
      "Todo vive en la Central, con métricas a la vista. Acompañamos, ajustamos y evolucionamos contigo — respondiendo como dueños.",

    "dif.rotulo": "Por qué Vorto",
    "dif.titulo": "Respondemos como dueños, no como proveedor de horas.",
    "dif.d1.t": "Equipo reducido y sénior",
    "dif.d1.d":
      "Hablas directo con quien lo construye. Sin capa de gestión de proyectos que atravesar ni becario aprendiendo en tu contrato.",
    "dif.d2.t": "Enfocados en resultados",
    "dif.d2.d":
      "Hablamos de pedidos que entran solos y de días de trabajo ahorrados — no de horas facturadas. El objetivo es tuyo; nosotros cuidamos el camino.",
    "dif.d3.t": "De punta a punta",
    "dif.d3.d":
      "Del primer diagnóstico a la operación en marcha, es el mismo equipo. Nada de pasar la bola a otro cuando toca sostener lo hecho.",
    "dif.d4.t": "Respuestas al instante",
    "dif.d4.d":
      "Transformación digital que responde rápido. Un problema en producción no se pierde en una fila — se vuelve prioridad.",

    "seg.rotulo": "Seguridad",
    "seg.titulo": "La confianza no se promete, se comprueba.",
    "seg.desc":
      "Cuando se trata de los datos de tu empresa, la vara es la de un comprador enterprise. Trabajamos dentro de tus reglas — y dejamos todo registrado.",
    "seg.s1": "Cumplimiento de las leyes de protección de datos",
    "seg.s2": "Tus datos no entrenan modelos",
    "seg.s3": "Control de acceso y SSO",
    "seg.s4": "Código e infraestructura auditables",

    "case.rotulo": "Resultado",
    "case.titulo":
      "Un agente de Vorto ya opera la posventa de una concesionaria.",
    "case.sub":
      "Un agente de IA especialista que responde en el WhatsApp del equipo — con el conocimiento técnico de la operación, al instante y con la fuente.",
    "case.quote":
      "«Antes paraba la máquina y esperaba horas por una respuesta. Ahora escribo por WhatsApp y sigo con la reparación.»",
    "case.nome": "Carlos",
    "case.cargo": "Mecánico de campo · Concesionaria",
    "case.m1.v": "+2.000",
    "case.m1.l": "interacciones por semana en WhatsApp",
    "case.m2.v": "80%",
    "case.m2.l": "de las dudas resueltas sin acudir al especialista",
    "case.m3.v": "~12 min",
    "case.m3.l": "ahorrados en cada consulta técnica",
    "case.mini":
      "+180 profesionales usándolo · 3× más rápido para encontrar la pieza · −55% de escalamientos · 24/7 en WhatsApp",

    "faq.rotulo": "Preguntas",
    "faq.titulo": "Preguntas que siempre aparecen.",
    "faq.q1.q": "¿Son una agencia o una consultoría?",
    "faq.q1.a":
      "Ninguna de las dos. Somos una software house: construimos el software y automatizamos la operación de punta a punta, con un equipo reducido y sénior que responde como dueño del resultado — no como proveedor de horas.",
    "faq.q2.q": "¿Necesito un equipo de tecnología interno?",
    "faq.q2.a":
      "No. Vorto existe justamente para empresas que necesitan modernizarse pero no tienen (ni quieren montar) un equipo de tecnología. Nosotros nos encargamos de la construcción y la operación; tú mantienes el control desde la Central.",
    "faq.q3.q": "¿Cuánto tiempo hasta ver algo funcionando?",
    "faq.q3.a":
      "Trabajamos en entregas de semanas, no de meses. Tras el diagnóstico, empezamos por el módulo que paga el proyecto más rápido — el primer agente suele salir en las primeras semanas.",
    "faq.q4.q": "¿Los agentes de IA usan los datos de mi empresa para entrenar modelos?",
    "faq.q4.a":
      "No. Tus datos no entrenan modelos. Trabajamos dentro de tus reglas de seguridad, en línea con las leyes de protección de datos, con control de acceso y todo registrado para auditoría.",
    "faq.q5.q": "¿Y si la IA se equivoca? ¿Quién manda?",
    "faq.q5.a":
      "Tú. Cada agente tiene reglas claras de cuándo puede actuar solo y cuándo necesita aprobación humana. Todo pasa por la Central, donde acompañas, apruebas y ajustas.",
    "faq.q6.q": "¿Se integran con los sistemas que ya usamos?",
    "faq.q6.a":
      "Sí. Construimos sobre lo que ya tienes — ERP, hojas de cálculo, herramientas de atención — en lugar de exigir que todos cambien de sistema. La automatización entra en tu flujo actual.",

    "cta.rotulo": "Hablemos",
    "cta.titulo": "¿Listo para el giro digital de tu empresa?",
    "cta.desc":
      "Cuéntanos el cuello de botella que más te molesta hoy. Te señalamos por dónde empezar y qué resuelve primero la automatización — sin compromiso.",
    "cta.btn2": "Agendar una llamada",
    "cta.form.nome": "Nombre",
    "cta.form.nomePh": "Tu nombre",
    "cta.form.email": "Correo",
    "cta.form.emailPh": "tu@empresa.com",
    "cta.form.empresa": "Empresa",
    "cta.form.empresaPh": "Opcional",
    "cta.form.msg": "¿Cuál es tu mayor cuello de botella hoy?",
    "cta.form.msgPh":
      "Cuéntanos en una o dos frases qué consume más tiempo del equipo.",
    "cta.form.enviar": "Enviar mensaje",
    "cta.form.enviando": "Enviando…",
    "cta.form.ok": "¡Recibido! Te respondemos pronto.",
    "cta.form.erro":
      "Algo salió mal. Inténtalo de nuevo o escribe a contact@vortolabs.com.",
    "cta.form.invalido": "Completa nombre, un correo válido y un mensaje.",

    "footer.sobre": "Software house con productos corporativos. Software que hace funcionar empresas.",
    "footer.h1": "Navegación",
    "footer.h2": "Empresa",
    "footer.h3": "Contacto",
    "footer.results": "Resultados",
    "footer.contact": "Contacto",
    "footer.base": "© 2026 Vorto Labs. Todos los derechos reservados.",
  },
};
